// MCHD Room Scheduler - Building Statistics Component
// Displays overview statistics for buildings

import React from 'react';
import {
  Group,
  Paper,
  Text,
  Badge,
  Stack,
  SimpleGrid,
  ThemeIcon,
  Progress,
} from '@mantine/core';
import {
  IconBuilding,
  IconBuildingWarehouse,
  IconDoor,
  IconCalendarEvent,
  IconTrendingUp,
  IconTrendingDown,
} from '@tabler/icons-react';
import { BuildingWithStats } from '../../api/endpoints/buildings';

// =============================================================================
// TYPES
// =============================================================================

interface BuildingStatsProps {
  buildings: BuildingWithStats[];
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  description?: string;
}

// =============================================================================
// STAT CARD COMPONENT
// =============================================================================

/**
 * Stat Card Component
 * Displays a single statistic with icon and optional trend
 */
function StatCard({
  title,
  value,
  icon,
  color,
  trend,
  description,
}: StatCardProps): React.ReactElement {
  return (
    <Paper p="md" radius="md" withBorder>
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start">
          <div>
            <Text size="sm" color="dimmed" fw={500}>
              {title}
            </Text>
            <Text size="xl" fw={700}>
              {value}
            </Text>
            {description && (
              <Text size="xs" color="dimmed">
                {description}
              </Text>
            )}
          </div>
          
          <ThemeIcon color={color} variant="light" size="lg">
            {icon}
          </ThemeIcon>
        </Group>
        
        {trend && (
            <Group gap="xs">
            {trend.isPositive ? (
              <IconTrendingUp size={16} color="green" />
            ) : (
              <IconTrendingDown size={16} color="red" />
            )}
            <Text
              size="sm"
              color={trend.isPositive ? 'green' : 'red'}
              fw={500}
            >
              {Math.abs(trend.value)}%
            </Text>
          </Group>
        )}
      </Stack>
    </Paper>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * Building Statistics Component
 * Displays overview statistics for buildings
 * 
 * @param props - Component props
 * @returns Building statistics interface
 */
export function BuildingStats({ buildings }: BuildingStatsProps): React.ReactElement {
  // =============================================================================
  // CALCULATIONS
  // =============================================================================
  
  const totalBuildings = buildings.length;
  const activeBuildings = buildings.filter(b => b.isActive).length;
  const inactiveBuildings = totalBuildings - activeBuildings;
  
  const totalFloors = buildings.reduce((sum, b) => sum + (b._count?.floors || 0), 0);
  const totalRooms = buildings.reduce((sum, b) => sum + ((b as any).roomsCount || 0), 0);
  const totalHolidays = buildings.reduce((sum, b) => sum + ((b as any).holidaysCount || 0), 0);
  
  const activeRooms = buildings.reduce((sum, b) => {
    // This would need to be calculated from room data
    // For now, we'll estimate based on building status
    return sum + (b.isActive ? ((b as any).roomsCount || 0) : 0);
  }, 0);
  
  const utilizationRate = totalRooms > 0 ? Math.round((activeRooms / totalRooms) * 100) : 0;
  const activeBuildingRate = totalBuildings > 0 ? Math.round((activeBuildings / totalBuildings) * 100) : 0;

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <Stack gap="md">
      {/* Main Statistics */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
        <StatCard
          title="Total Buildings"
          value={totalBuildings}
          icon={<IconBuilding size={20} />}
          color="blue"
          description={`${activeBuildings} active, ${inactiveBuildings} inactive`}
        />
        
        <StatCard
          title="Total Floors"
          value={totalFloors}
          icon={<IconBuildingWarehouse size={20} />}
          color="green"
          description={`Across all buildings`}
        />
        
        <StatCard
          title="Total Rooms"
          value={totalRooms}
          icon={<IconDoor size={20} />}
          color="orange"
          description={`${activeRooms} active rooms`}
        />
        
        <StatCard
          title="Holidays"
          value={totalHolidays}
          icon={<IconCalendarEvent size={20} />}
          color="purple"
          description={`Scheduled holidays`}
        />
      </SimpleGrid>

      {/* Progress Indicators */}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Paper p="md" radius="md" withBorder>
          <Stack gap="sm">
            <Group justify="space-between">
              <Text size="sm" fw={500}>
                Building Utilization
              </Text>
              <Badge color={activeBuildingRate > 80 ? 'green' : activeBuildingRate > 60 ? 'yellow' : 'red'}>
                {activeBuildingRate}%
              </Badge>
            </Group>
            <Progress
              value={activeBuildingRate}
              color={activeBuildingRate > 80 ? 'green' : activeBuildingRate > 60 ? 'yellow' : 'red'}
              size="sm"
            />
            <Text size="xs" color="dimmed">
              {activeBuildings} of {totalBuildings} buildings are active
            </Text>
          </Stack>
        </Paper>

        <Paper p="md" radius="md" withBorder>
          <Stack gap="sm">
            <Group justify="space-between">
              <Text size="sm" fw={500}>
                Room Utilization
              </Text>
              <Badge color={utilizationRate > 80 ? 'green' : utilizationRate > 60 ? 'yellow' : 'red'}>
                {utilizationRate}%
              </Badge>
            </Group>
            <Progress
              value={utilizationRate}
              color={utilizationRate > 80 ? 'green' : utilizationRate > 60 ? 'yellow' : 'red'}
              size="sm"
            />
            <Text size="xs" color="dimmed">
              {activeRooms} of {totalRooms} rooms are active
            </Text>
          </Stack>
        </Paper>
      </SimpleGrid>

      {/* Quick Stats */}
      {buildings.length > 0 && (
        <Paper p="md" radius="md" withBorder>
          <Text size="sm" fw={500} mb="sm">
            Quick Stats
          </Text>
          <Group gap="xl">
            <div>
              <Text size="xs" color="dimmed">
                Average floors per building
              </Text>
              <Text size="lg" fw={600}>
                {totalBuildings > 0 ? Math.round(totalFloors / totalBuildings) : 0}
              </Text>
            </div>
            
            <div>
              <Text size="xs" color="dimmed">
                Average rooms per building
              </Text>
              <Text size="lg" fw={600}>
                {totalBuildings > 0 ? Math.round(totalRooms / totalBuildings) : 0}
              </Text>
            </div>
            
            <div>
              <Text size="xs" color="dimmed">
                Average holidays per building
              </Text>
              <Text size="lg" fw={600}>
                {totalBuildings > 0 ? Math.round(totalHolidays / totalBuildings) : 0}
              </Text>
            </div>
          </Group>
        </Paper>
      )}
    </Stack>
  );
}
